import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import {
  bitmaskToStrings,
  stringsToBitmask,
  checkPermission,
  hasWildcard as hasWildcardPerm,
} from '../../utils/permissionUtils';
import MultiSelect, { type MultiSelectOption } from '../../components/MultiSelect';
import ToggleSwitch from '../../components/forms/ToggleSwitch';
import { formatEventOption, sortEventsByDate } from '../../utils/eventUtils';
import Button from '../../components/ui/Button';
import { SkeletonTable } from '../../components/ui/Skeleton';
import '../../App.css';
import './RolesPage.css';
import { translateError, errorCode } from '../../utils/errorMessages';

interface PermissionsData {
  all: string[];
  grouped: Record<string, string[]>;
  defaults?: Record<string, Record<string, number>>;
}

const PERMISSION_CATEGORY_ORDER = [
  'members', 'groups', 'events', 'templates', 'attendance',
  'activities', 'memberships',
  // 'organization', not 'org': the category key is the RESOURCE name, and the
  // old 'org' entry never matched anything, so Organization sorted last and
  // rendered with its raw key as the heading.
  'payments', 'organization', 'users', 'roles', 'statistics', 'requirements',
  'announcements',
];

const USER_TYPE_TOGGLEABLE = ['members:write', 'members:delete'];

const RoleCreatePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { currentMembership } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<'admin' | 'user'>('admin');
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());
  const [limitDataAccess, setLimitDataAccess] = useState(false);
  const [accessGrants, setAccessGrants] = useState<{
    activities: number[];
    groups: number[];
    members: number[];
    events: number[];
    eventTemplates: number[];
  }>({ activities: [], groups: [], members: [], events: [], eventTemplates: [] });
  const [advancedGrantsOpen, setAdvancedGrantsOpen] = useState(false);

  const [permissionsData, setPermissionsData] = useState<PermissionsData | null>(null);
  const [activityOptions, setActivityOptions] = useState<MultiSelectOption[]>([]);
  const [groupOptions, setGroupOptions] = useState<MultiSelectOption[]>([]);
  const [memberOptions, setMemberOptions] = useState<MultiSelectOption[]>([]);
  const [eventOptions, setEventOptions] = useState<MultiSelectOption[]>([]);
  const [templateOptions, setTemplateOptions] = useState<MultiSelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [grantScopeError, setGrantScopeError] = useState(false);

  const myPerms = currentMembership?.role?.permissions;
  const myHasWildcard = hasWildcardPerm(myPerms);
  // A data-limited creator may only ever produce data-limited roles.
  const iAmScoped = Boolean(currentMembership?.role?.limitDataAccess) && !myHasWildcard;

  // Load permissions data and entity options
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [permsRes, activitiesRes, groupsRes, membersRes, eventsRes, templatesRes] = await Promise.all([
        api.roles.getPermissions(),
        api.activities.list(),
        api.groups.list(),
        api.members.list(),
        api.events.list(),
        api.eventTemplates.list(),
      ]);
      setPermissionsData(permsRes.data as PermissionsData);
      setActivityOptions((activitiesRes.data as any[]).map((a: any) => ({ _id: a._id, name: a.name })));
      setGroupOptions((groupsRes.data as any[]).map((g: any) => ({ _id: g._id, name: g.name })));
      setMemberOptions((membersRes.data as any[]).map((m: any) => ({ _id: m._id, name: m.name })));
      const sortedEvents = sortEventsByDate(eventsRes.data as any[]);
      setEventOptions(sortedEvents.map((e: any) => ({ _id: e._id, name: formatEventOption(e, i18n.language) })));
      setTemplateOptions((templatesRes.data as any[]).map((t: any) => ({ _id: t._id, name: t.name })));
    } catch (err: any) {
      addToast(translateError(err, t), 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Apply default permissions when type changes or after initial data load
  useEffect(() => {
    if (permissionsData?.defaults?.[type]) {
      setSelectedPermissions(new Set(bitmaskToStrings(permissionsData.defaults[type])));
    }
  }, [type, permissionsData]);

  const canEditPermission = useCallback((permission: string): boolean => {
    if (type === 'user') return USER_TYPE_TOGGLEABLE.includes(permission);
    if (myHasWildcard) return true;
    return myPerms ? checkPermission(myPerms, permission) : false;
  }, [type, myHasWildcard, myPerms]);

  const handleToggle = (permission: string) => {
    if (!canEditPermission(permission)) return;
    setSelectedPermissions(prev => {
      const next = new Set(prev);
      if (next.has(permission)) {
        next.delete(permission);
      } else {
        next.add(permission);
      }
      return next;
    });
  };

  const sortedCategories = useMemo(() => {
    if (!permissionsData?.grouped) return [];
    const categories = Object.keys(permissionsData.grouped);
    return categories.sort((a, b) => {
      const ai = PERMISSION_CATEGORY_ORDER.indexOf(a);
      const bi = PERMISSION_CATEGORY_ORDER.indexOf(b);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  }, [permissionsData]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      addToast(t('roles.nameRequired') || 'Role name is required', 'warning');
      return;
    }
    if (selectedPermissions.size === 0) {
      addToast(t('roles.atLeastOnePermission'), 'warning');
      return;
    }

    setSaving(true);
    setGrantScopeError(false);
    try {
      const permsBitmask = stringsToBitmask(Array.from(selectedPermissions));
      await api.roles.create({
        name: name.trim(),
        description: description.trim() || undefined,
        permissions: permsBitmask,
        type,
        limitDataAccess: iAmScoped ? true : limitDataAccess,
        accessGrants: limitDataAccess ? accessGrants : undefined,
      });
      addToast(t('roles.createSuccess'), 'success');
      navigate('/admin/roles');
    } catch (err: any) {
      if (errorCode(err) === 'ROLE_GRANT_OUTSIDE_SCOPE') {
        setGrantScopeError(true);
        addToast(t('roles.grantOutsideScope'), 'error');
      } else {
        addToast(translateError(err, t, 'common.error'), 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-list-page">
        <SkeletonTable rows={5} columns={3} />
      </div>
    );
  }

  return (
    <div className="admin-list-page">
      <div className="role-detail-back">
        <Link to="/admin/roles" className="back-link">&larr; {t('common.back')}</Link>
      </div>

      {/* Header */}
      <div className="role-detail-header">
        <h2>{t('roles.createTitle')}</h2>
      </div>

      {/* Name & Description */}
      <div className="role-form-narrow">
        <div className="role-edit-fields">
          <div className="form-group">
            <label htmlFor="role-name">{t('roles.nameLabel')}</label>
            <input
              id="role-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={t('roles.namePlaceholder')}
              maxLength={50}
            />
          </div>
          <div className="form-group">
            <label htmlFor="role-description">{t('roles.descriptionLabel')}</label>
            <textarea
              id="role-description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={t('roles.descriptionPlaceholder')}
              maxLength={255}
              rows={2}
            />
          </div>
        </div>

        {/* Type selector */}
        <div className="form-group" style={{ marginBottom: '1.5rem' }}>
          <label>{t('roles.typeLabel')}</label>
          <div className="role-type-options">
            <label className="role-type-option">
              <input
                type="radio"
                name="roleType"
                value="admin"
                checked={type === 'admin'}
                onChange={() => setType('admin')}
              />
              <div>
                <strong>{t('roles.types.admin')}</strong>
                <p>{t('roles.typeAdminDescription')}</p>
              </div>
            </label>
            <label className="role-type-option">
              <input
                type="radio"
                name="roleType"
                value="user"
                checked={type === 'user'}
                onChange={() => setType('user')}
              />
              <div>
                <strong>{t('roles.types.user')}</strong>
                <p>{t('roles.typeUserDescription')}</p>
              </div>
            </label>
          </div>
        </div>
      </div>

      {/* Info banner for user type */}
      {type === 'user' && (
        <div className="role-info-banner role-info-banner--warning">
          <i className="fas fa-exclamation-triangle"></i>
          {t('roles.userTypeRestriction')}
        </div>
      )}

      {/* Permissions grid */}
      <div className="permissions-grid">
        {sortedCategories.map(category => {
          const perms = permissionsData!.grouped[category]!;
          return (
            <div key={category} className="permission-category">
              <h4>{t(`roles.permissionCategories.${category}`, category)}</h4>
              {perms.map(perm => {
                const isChecked = selectedPermissions.has(perm);
                const isEditable = canEditPermission(perm);
                const action = perm.split(':')[1] || perm;
                return (
                  <div
                    key={perm}
                    className={`permission-label ${isEditable ? 'permission-label--editable' : 'permission-label--readonly'}`}
                  >
                    <ToggleSwitch
                      checked={isChecked}
                      disabled={!isEditable}
                      onChange={() => handleToggle(perm)}
                      label={<span>{action}</span>}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Limit Data Access */}
      <div className="form-group" style={{ marginTop: '1.5rem' }}>
        <ToggleSwitch
          checked={limitDataAccess}
          onChange={setLimitDataAccess}
          // See RoleDetailPage: a scoped creator cannot mint an unrestricted
          // role, because that is access it does not itself hold.
          disabled={iAmScoped}
          label={
            <div>
              <strong>{t('roles.limitDataAccess')}</strong>
              <p className="form-help-text">
                {iAmScoped
                  ? t('roles.limitDataAccessLockedToScope')
                  : t('roles.limitDataAccessDescription')}
              </p>
            </div>
          }
        />
      </div>

      {/* Access Grants */}
      {limitDataAccess && (
        <div className="form-group access-grants-section">
          <label>{t('roles.accessGrants')}</label>
          {/* Primary scope: Activities */}
          <div className="access-grants-grid">
            <MultiSelect
              label={t('roles.activityAccess')}
              options={activityOptions}
              selectedIds={accessGrants.activities}
              onChange={(ids) => setAccessGrants(prev => ({ ...prev, activities: ids.map(Number) }))}
              placeholder={t('roles.activityAccess')}
            />
          </div>
          <small className="form-help-text">{t('roles.activityAccessDescription')}</small>

          {/* Advanced: per-entity grants (overrides) */}
          <div style={{ marginTop: '1rem' }}>
            <button
              type="button"
              className="collapsible-toggle"
              onClick={() => setAdvancedGrantsOpen(v => !v)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 'inherit', color: 'inherit' }}
            >
              {advancedGrantsOpen ? '▾' : '▸'} {t('roles.advancedPerEntityGrants')}
            </button>
            {advancedGrantsOpen && (
              <div className="access-grants-grid" style={{ marginTop: '0.5rem' }}>
                <MultiSelect
                  label={t('roles.accessGroups')}
                  options={groupOptions}
                  selectedIds={accessGrants.groups}
                  onChange={(ids) => setAccessGrants(prev => ({ ...prev, groups: ids.map(Number) }))}
                  placeholder={t('roles.accessGroups')}
                />
                <MultiSelect
                  label={t('roles.accessMembers')}
                  options={memberOptions}
                  selectedIds={accessGrants.members}
                  onChange={(ids) => setAccessGrants(prev => ({ ...prev, members: ids.map(Number) }))}
                  placeholder={t('roles.accessMembers')}
                />
                <MultiSelect
                  label={t('roles.accessEventTemplates')}
                  options={templateOptions}
                  selectedIds={accessGrants.eventTemplates}
                  onChange={(ids) => setAccessGrants(prev => ({ ...prev, eventTemplates: ids.map(Number) }))}
                  placeholder={t('roles.accessEventTemplates')}
                />
                <MultiSelect
                  label={t('roles.accessEvents')}
                  options={eventOptions}
                  selectedIds={accessGrants.events}
                  onChange={(ids) => setAccessGrants(prev => ({ ...prev, events: ids.map(Number) }))}
                  placeholder={t('roles.accessEvents')}
                />
              </div>
            )}
            {advancedGrantsOpen && (
              <small className="form-help-text">{t('roles.accessGroupsDescription')}</small>
            )}
          </div>
        </div>
      )}

      {grantScopeError && (
        <div className="role-info-banner role-info-banner--warning" role="alert">
          <i className="fas fa-exclamation-triangle"></i>
          {t('roles.grantOutsideScope')}
        </div>
      )}

      {/* Actions */}
      <div className="role-detail-actions">
        <Button onClick={handleSubmit} loading={saving}>
          {t('roles.createRole')}
        </Button>
      </div>
    </div>
  );
};

export default RoleCreatePage;
